import { Component, EventEmitter, Output } from "@angular/core";
import { MenuController, ModalController, Platform } from "@ionic/angular";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { NavigationEnd } from "@angular/router";
import { register } from "swiper/element/bundle";
import { TechnicalSupportComponent } from "./components/dialogs/technicalsupport/technical-support.component";
import { AlertService } from "./providers/alert/alert.service";
import { ParserService } from "./providers/parser/parser.service";
import { LocalStorageService } from "./providers/local-storage/local-storage.service";
import { forkJoin } from "rxjs";
import { RequestProviderService } from "./providers/request-provider/request-provider.service";
import { App, BackButtonListener } from "@capacitor/app";
import { Location } from "@angular/common";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { APPSTORE_URL, FEATURES, PLAYSTORE_URL, SHOW_APP_STORE_LINKS_IN_WEB_VERSION } from "src/environments/environment";
import { SafeArea } from "capacitor-plugin-safe-area";

// Globally register Swiper custom elements
register();

const noSideMenuURLs = [
  "/login",
  "/forgot-password",
  "/app-imprint",
  "/app-privacy",
  "/app-terms-and-conditions",
  "/landing",
] as const;

const NAVIGATION = [
  {
    title: "PAGES.HOME",
    url: "/home",
    icon: "home-outline",
    id: "navHome", // Don't change IDs!! Used for testing.
  },
  {
    title: "PAGES.MY_INTERVENTIONS",
    url: "/intervention-overview",
    icon: "clipboard-outline",
    id: "navInterventions",
  },
  {
    title: "PAGES.MY_DIARIES",
    url: "/diary-overview",
    icon: "book-outline",
    id: "navDiaries",
  },
  {
    title: "PAGES.CHAT",
    url: "/feedback-overview",
    icon: "people-outline",
    id: "navChat",
  },
  {
    title: "PAGES.INVITATIONS",
    url: "/invitations",
    icon: "paper-plane-outline",
    id: "navInvitations",
  },
  {
    title: "PAGES.MY_PROGRESS",
    url: "/progress",
    icon: "podium-outline",
    id: "navProgress",
  },
  {
    title: "PAGES.MY_PROFILE",
    url: "/settings",
    icon: "person-outline",
    id: "navProfile",
  },
] as const;

type SideMenuNavigation = typeof NAVIGATION;
type SideMenuNavigationUrl = SideMenuNavigation[number]["url"];

@Component({
    selector: "app-root",
    templateUrl: "app.component.html",
    styleUrls: ["app.component.scss"],
    standalone: false
})
export class AppComponent {
  navigation: SideMenuNavigation;
  previousUrl: string;
  smallScreen: boolean = false;
  private skillsAllowed = FEATURES.includes("skills");
  private sideMenuDataNotLoaded = true;
  @Output("routeEmitter") routeEmitter = new EventEmitter<string>();

  showAppStoreLinks = SHOW_APP_STORE_LINKS_IN_WEB_VERSION && !Capacitor.isNativePlatform();
  appstore_url = APPSTORE_URL;
  playstore_url = PLAYSTORE_URL;

  /**
   * A flag to track whether a back button click occurred within a specific timeout period.
   * Used to determine if the app should exit when the user double-clicks the back button.
   * The default value is `false`.
   */
  private backClickWithinTimeout: boolean = false;

  /**
   * A handle for managing the back button listener added by the Capacitor App plugin.
   * Used to store the reference to the currently active back button listener so it can be removed when needed.
   * 
   * @optional
   */
  private backButtonListenerHandle?: PluginListenerHandle;

  constructor(
    private platform: Platform,
    private router: Router,
    private menuCtrl: MenuController,
    private alertService: AlertService,
    private translateService: TranslateService,
    private modalController: ModalController,
    private parserService: ParserService,
    private localStorage: LocalStorageService,
    private requestProvider: RequestProviderService,
    private location: Location,
  ) {
    this.navigation = NAVIGATION;
    this.initializeApp();
    this.registerNoMenuPages();
    router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.backClickWithinTimeout = false;
        this.previousUrl = event.url;
        if (this.sideMenuDataNotLoaded) {
          const currentUrl = event.url;
          if (currentUrl.startsWith("/home")) {
            // "/home" always sets data for side menu
            this.sideMenuDataNotLoaded = false;
          } else {
            const previousNavigation = router.currentNavigation()?.previousNavigation;
            const previousUrl = previousNavigation?.initialUrl.toString();
            // previousNavigation === null: After refreshing page or directly accessing page while logged in
            // previousUrl.startsWith(...): After refreshing page or directly accessing page while logged out, and then forwarded from login-page
            if (
              (previousNavigation === null || previousUrl.startsWith("/login")) &&
              this.urlHasSideMenu(currentUrl)
            ) {
              this.loadSideMenuState(currentUrl);
            }
          }
        }
      });
  }

  loadSideMenuState(currentUrl: string) {
    const reqs = {
      interventions: this.requestProvider.getMyInterventions(),
      diaryInstances: this.requestProvider.getMyDiaryInstances(),
      instances: this.requestProvider.getMyInstances(),
      messages: this.requestProvider.getMyMessages(),
      feedback: this.requestProvider.getMyFeedback(),
      announcements: this.requestProvider.getAnnouncements(),
      activities: this.requestProvider.getMyActivities(),
      invitations: this.requestProvider.getMyInvitations(),
    };
    forkJoin(reqs).subscribe({
      next: async (results) => {
        this.parserService.parseDiaryInstances(results.diaryInstances, true);
        const interventions = this.parserService.parseInterventions(results.interventions);
        this.parserService.parseInstances(results.instances, interventions);
        this.parserService.setupThreadsState(results.messages, results.feedback);
        const invitations = this.parserService.parseInvitations(results.invitations);
        const newAnnouncements = this.parserService.parseAnnouncements(results.announcements);
        //if there is a new announcement select that & show announcement even if currently hidden by user
        //otherwise show currently selected one, except on login (first)
        if (newAnnouncements.length > this.parserService.announcements.length) {
          this.parserService.selectedAnnouncement = newAnnouncements.length - 1;
          this.parserService.showAnnouncements = true;
        } else if (newAnnouncements.length === 0) {
          this.parserService.selectedAnnouncement = 0;
          this.parserService.showAnnouncements = false;
        }
        this.parserService.announcements = newAnnouncements;
        this.parserService.parseActivities(results.activities);
        if (invitations.length > 0) {
          if (!currentUrl.startsWith("/invitations")) {
            this.alertService.showOpenInvitations(this.router);
          } else {
            this.alertService.openInvitationsNeverShown = false;
          }
        }
        this.sideMenuDataNotLoaded = false;
      },
      error: (_error) => { },
    });
  }

  initializeApp() {
    document.body.classList.toggle("darkTheme", true);
    this.translateService.setDefaultLang(this.localStorage.getAppLanguage());

    this.platform.ready().then(() => {
      if (this.platform.is("android") || this.platform.is("ios")) {
        this.initializeScssVariables();
        this.setStatusbarStyle();
        this.applyDefaultBackButtonListener();
      }

      if (this.platform.width() < 992) {
        this.smallScreen = true;
      }

      this.platform.resize.subscribe(() => {
        this.smallScreen = this.platform.width() < 992;
      });
    });
  }

  registerNoMenuPages() {
    this.router.events.subscribe((event) => {
      if (this.localStorage.getIsInAppBrowser()) {
        this.menuCtrl.enable(false);
      } else if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects;
        const showMenu = this.urlHasSideMenu(url);
        this.menuCtrl.enable(showMenu);
      }
    });
  }

  private urlHasSideMenu(url: string): boolean {
    return (
      url !== "/" &&
      !url.startsWith("/?") &&
      noSideMenuURLs.every((u) => !url.startsWith(u))
    );
  }

  routeToUrl(url: string, event: Event) {
    event.stopPropagation();
    if (this.previousUrl.includes("/lesson/")) {
      this.routeEmitter.emit(url);
    } else {
      this.router.navigateByUrl(url, { state: { previousUrl: this.previousUrl }});
    }
    this.menuCtrl.close();
  }

  closeMenu() {
    this.menuCtrl.close();
  }

  showPage(url: SideMenuNavigationUrl): boolean {
    switch (url) {
      case "/diary-overview":
        return this.parserService.showDiaries;
      case "/feedback-overview":
        return this.parserService.showConversations;
      case "/invitations":
        return this.parserService.invitations.length > 0;
      case "/progress":
        return this.skillsAllowed && this.parserService.showProgress;
      default:
        return true;
    }
  }

  async openEmailSupport() {
    this.overwriteDefaultBackButtonListener(() => {});
    const modal = await this.modalController.create({
      component: TechnicalSupportComponent,
      cssClass: "techModal",
    });
    return await modal.present();
  }

  /**
   * Replaces the default back button behavior with a custom listener function only on Android.
   * If a previous listener is set, it will be removed before adding the new one.
   * 
   * @param listenerFunction - A custom listener function that handles the back button event.
   * @returns A Promise that resolves once the listener is added.
   */
  public async overwriteDefaultBackButtonListener(listenerFunction: BackButtonListener): Promise<void> {
    if (Capacitor.getPlatform() === "android") {
      this.backButtonListenerHandle?.remove();
      this.backButtonListenerHandle = await App.addListener("backButton", listenerFunction);
    }
  }

  /**
   * Applies the default back button behavior for the app only on Android.
   * If the current route is the root or login page, a double-back click within a timeout will exit the app.
   * Otherwise, the app navigates back in history.
   * 
   * If a previous listener is set, it will be removed before applying the default listener.
   * A toast warning is shown to the user to confirm exiting the app when double-back click is required.
   * 
   * @returns A Promise that resolves once the listener is added.
   */
  public async applyDefaultBackButtonListener(): Promise<void> {
    if (Capacitor.getPlatform() === "android") {
      this.backButtonListenerHandle?.remove();
      this.backButtonListenerHandle = await App.addListener("backButton", () => {
        if (this.router.url === "/" || this.router.url === "/login" || (this.location.getState() as any)?.appCanBeClosed) {
          if (this.backClickWithinTimeout) {
            App.exitApp();
          } else {
            this.backClickWithinTimeout = true;
            this.alertService.presentToast("WARNING.WARNING_BACK_CLICK_TO_CLOSE_APP");
            setTimeout(() => {
              this.backClickWithinTimeout = false;
            }, 5000);
          }
        } else {
          this.location.back();
        }
      });
    }
  }

  get language() {
    return this.translateService.currentLang;
  }

  /**
   * Initializes custom CSS variables for safe area insets and listens for changes.
   * * This function provides a robust alternative to using the default Ionic SCSS variables 
   * (`--ion-safe-area-*`). These Ionic variables rely on the standard CSS environment 
   * variables (`safe-area-inset-*`), which are known to be unreliable in the 
   * Capacitor/Android environment, often failing to initialize correctly or suffering 
   * from a timing issue (race condition) that temporarily sets their value to 0px.
   * * Instead, this method reads the **correct, dynamic** insets directly from the 
   * native `SafeArea` plugin and sets **alternative, protected CSS variables** (`--safe-area-top`, `--safe-area-bottom`, etc.) on the document's root element.
   * These custom variables reliably hold the correct safe area values, preventing 
   * faulty Webview-derived values from overwriting them.
   * @private
   * @async
   * @returns {Promise<void>}
   */
  private async initializeScssVariables(): Promise<void> {
    SafeArea.getSafeAreaInsets().then(({ insets }) => {
      console.log(insets);
      for (const [key, value] of Object.entries(insets)) {
        document.documentElement.style.setProperty(
          `--safe-area-${key}`,
          `${value}px`,
        );
      }
    });

    SafeArea.getStatusBarHeight().then(({ statusBarHeight }) => {
      console.log(statusBarHeight, 'statusbarHeight');
    });

    await SafeArea.removeAllListeners();

    // when safe-area changed
    await SafeArea.addListener('safeAreaChanged', data => {
      const { insets } = data;
      for (const [key, value] of Object.entries(insets)) {
        document.documentElement.style.setProperty(
          `--safe-area-${key}`,
          `${value}px`,
        );
      }
    });
  }

  private setStatusbarStyle() {
    const theme = this.localStorage.getTheme()
    StatusBar.setStyle({style: theme === "0" ? Style.Dark : Style.Light});
  }
}
